import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Checkbox,
  FormControlLabel,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import AuthService from '../services/AuthService';

// Bumping this string re-gates every existing user on next login (their
// stored termsAcceptedAt/termsVersion no longer matches CURRENT_TERMS_VERSION)
// without needing a new DB column -- see User.termsVersion's own comment.
export const CURRENT_TERMS_VERSION = '2026-09-24-v1';

// PLACEHOLDER LEGAL TEXT -- drafted as reasonable generic SaaS terms so the
// acceptance GATE and flow can be built and tested end to end now. This is
// NOT reviewed or approved legal language and must be replaced with real
// Terms & Conditions from counsel before this gate is relied on in
// production. Flagged explicitly to the product owner; do not treat as final.
const PLAIN_ENGLISH_SUMMARY = [
  'You’re creating an account for your company to use Foysonis Supply Chain Applications to manage your warehouse operations.',
  'Your data stays private to your company — other companies using Foysonis Supply Chain Applications can never see your inventory, documents, or configuration.',
  'You’re responsible for keeping your login credentials secure and for what happens under your account.',
  'Foysonis Supply Chain Applications is provided "as is" — we work hard to keep it reliable, but we can’t guarantee it’s always error-free or uninterrupted.',
  'You can stop using Foysonis Supply Chain Applications at any time; we’ll tell you if these terms ever change in a way that affects you.',
];

const LEGAL_TEXT = `
1. ACCEPTANCE OF TERMS
By creating an account and using the Foysonis Supply Chain Applications platform ("Service"), you agree to be bound by these Terms & Conditions.

2. ACCOUNT RESPONSIBILITIES
You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account. You agree to notify us promptly of any unauthorized use of your account.

3. DATA OWNERSHIP AND CONFIDENTIALITY
Your company's data (inventory records, documents, configuration, and other content you submit to the Service) remains your property. We will not share your data with, or expose it to, any other tenant/company using the Service. We may access your data as necessary to provide, maintain, and support the Service.

4. ACCEPTABLE USE
You agree not to use the Service to: (a) violate any applicable law; (b) attempt to gain unauthorized access to another tenant's data or to the Service's underlying systems; (c) interfere with or disrupt the integrity or performance of the Service; or (d) reverse-engineer, decompile, or attempt to extract the Service's underlying source code, prompts, or proprietary logic.

5. SERVICE AVAILABILITY
The Service is provided "as is" and "as available." We will use commercially reasonable efforts to maintain uptime and reliability, but we do not guarantee the Service will be uninterrupted, timely, secure, or error-free.

6. LIMITATION OF LIABILITY
To the maximum extent permitted by law, Foysonis shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the Service.

7. TERMINATION
You may stop using the Service at any time. We may suspend or terminate your access if you violate these Terms.

8. CHANGES TO THESE TERMS
We may update these Terms from time to time. If a change materially affects your rights, we will notify you and may require you to accept the updated Terms before continuing to use the Service.

9. CONTACT
Questions about these Terms can be directed to your account administrator or Foysonis support.
`.trim();

/**
 * Blocking, non-dismissable modal shown on first login before the facility
 * setup gate (see AtomApp.js's gating order: terms -> facility setup ->
 * app shell). Renders a plain-English summary ABOVE the full legal text --
 * per explicit product direction that business users shouldn't have to
 * parse legal language to understand what they're agreeing to.
 */
/**
 * mode="gate" (default): blocking first-login acceptance flow -- checkbox
 * required, calls AuthService.acceptTerms(), not dismissable.
 * mode="review": read-only reopen from Account Settings ("View Terms &
 * Conditions") -- no checkbox/accept action, just a Close button. Reuses the
 * exact same summary/legal-text content so there is only one place that
 * text is ever written.
 */
const TermsAcceptanceModal = ({ open, onAccept, mode = 'gate', onClose, acceptedAt, acceptedVersion }) => {
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const isReview = mode === 'review';

  const handleAccept = async () => {
    setSubmitting(true);
    setError('');
    try {
      const updatedUser = await AuthService.acceptTerms(CURRENT_TERMS_VERSION);
      onAccept(updatedUser);
    } catch (e) {
      setError(e.message || 'Failed to record acceptance. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={!isReview}
      onClose={isReview ? onClose : () => {}}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <GavelIcon color="action" />
        Terms &amp; Conditions
      </DialogTitle>
      <DialogContent dividers>
        {isReview && acceptedAt && (
          <Alert severity="success" sx={{ mb: 2 }}>
            You accepted version {acceptedVersion} on {new Date(acceptedAt).toLocaleDateString()}.
          </Alert>
        )}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          In plain English, here's what this means:
        </Typography>
        <List dense sx={{ mb: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          {PLAIN_ENGLISH_SUMMARY.map((line, idx) => (
            <ListItem key={idx} sx={{ py: 0.5 }}>
              <ListItemText primary={line} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          ))}
        </List>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Full Terms &amp; Conditions
        </Typography>
        <Box
          sx={{
            maxHeight: 220,
            overflowY: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1.5,
            mb: 2,
          }}
        >
          {/* Real bug found live 2026-10-01: component="pre" rendered as a bare <pre> tag,
              which collides with App.css's ".MuiTypography-root pre" rule (near-black
              background, meant only for markdown code blocks in chat messages) with no
              matching text-color override -- black text on a near-black box, reading as a
              solid black rectangle. whiteSpace: 'pre-wrap' below already preserves this
              plain legal text's line breaks without needing a real <pre> element, so
              dropping component="pre" removes the unintended CSS collision entirely. */}
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', m: 0 }}>
            {LEGAL_TEXT}
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!isReview && (
          <FormControlLabel
            control={<Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} />}
            label="I have read and agree to the Terms & Conditions"
          />
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        {isReview ? (
          <Button variant="outlined" onClick={onClose} fullWidth>
            Close
          </Button>
        ) : (
          <Button
            variant="contained"
            color="success"
            disabled={!checked || submitting}
            onClick={handleAccept}
            fullWidth
          >
            {submitting ? 'Saving…' : 'Accept & Continue'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TermsAcceptanceModal;
